import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUsers, FiEdit3, FiCopy, FiEye, FiEyeOff, FiExternalLink, FiCheck, FiX,
} from 'react-icons/fi';
import {
  listarNfClientes, salvarAcessoNfCliente, buscarSenhaNfCliente,
} from '../../services/nfClientes';
import styles from './Nf.module.css';
import useEsc from '../../hooks/useEsc';

/* Clipboard só existe em contexto seguro (https ou localhost). Em http a
   API nem aparece, então cai no truque do textarea + execCommand pra não
   deixar o botão de copiar morto. */
async function copiar(texto) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch { /* cai no plano B */ }
  try {
    const area = document.createElement('textarea');
    area.value = texto;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

/* botãozinho que vira ✓ por 1,5s depois de copiar -- sem isso não dá
   pra saber se o clique funcionou */
function BotaoCopiar({ aoObterTexto, titulo, desabilitado }) {
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState(false);

  async function clicar() {
    setErro(false);
    const texto = await aoObterTexto();
    if (!texto) { setErro(true); setTimeout(() => setErro(false), 1500); return; }
    const ok = await copiar(texto);
    if (!ok) { setErro(true); setTimeout(() => setErro(false), 1500); return; }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1500);
  }

  return (
    <button
      type="button"
      className={styles.btnIconeTabela}
      title={titulo}
      onClick={clicar}
      disabled={desabilitado}
    >
      {copiado ? <FiCheck size={13} color="#17a05f" /> : erro ? <FiX size={13} color="#d0453b" /> : <FiCopy size={13} />}
    </button>
  );
}

/* A senha fica cifrada no banco e não vem junto da listagem: só é buscada
   (e registrada na auditoria) quando alguém clica em ver ou copiar. */
function CelulaSenha({ item, aoErro }) {
  const [revelada, setRevelada] = useState(null);
  const [carregando, setCarregando] = useState(false);

  async function obterSenha() {
    if (revelada) return revelada;
    try {
      return await buscarSenhaNfCliente(item.id);
    } catch (e) {
      aoErro(e.message);
      return null;
    }
  }

  async function alternar() {
    if (revelada) { setRevelada(null); return; }
    setCarregando(true);
    try {
      const senha = await buscarSenhaNfCliente(item.id);
      setRevelada(senha);
    } catch (e) {
      aoErro(e.message);
    } finally {
      setCarregando(false);
    }
  }

  if (!item.tem_senha) return <span className={styles.celulaVazia}>—</span>;

  return (
    <div className={styles.celulaComAcoes}>
      <span className={revelada ? styles.senhaVisivel : styles.senhaOculta}>
        {carregando ? 'carregando...' : revelada || '••••••••'}
      </span>
      <button
        type="button"
        className={styles.btnIconeTabela}
        title={revelada ? 'Ocultar senha' : 'Ver senha'}
        onClick={alternar}
      >
        {revelada ? <FiEyeOff size={13} /> : <FiEye size={13} />}
      </button>
      <BotaoCopiar aoObterTexto={obterSenha} titulo="Copiar senha" />
    </div>
  );
}

/* Editar pelo modal, não direto na coluna: são 5 campos (um deles texto
   livre) e a senha precisa de tratamento à parte -- editar tudo inline
   deixaria a linha ilegível e é fácil salvar errado sem perceber. A
   tabela fica só pra leitura rápida, com copiar/ver à mão. */
function ModalAcesso({ item, onFechar, onSalvo }) {
  useEsc(onFechar);
  const [form, setForm] = useState({
    local_nf:   item.local_nf   || '',
    usuario:    item.usuario    || '',
    senha:      '',
    link:       item.link       || '',
    observacao: item.observacao || '',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [verSenha, setVerSenha] = useState(false);

  function set(campo, valor) { setForm(f => ({ ...f, [campo]: valor })); }

  async function salvar() {
    setSalvando(true); setErro('');
    try {
      await salvarAcessoNfCliente(item.id, form);
      onSalvo();
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }

  return (
    <div className={styles.modalFundo}>
      <div className={styles.modalCaixa} onClick={e => e.stopPropagation()}>
        <div className={styles.modalTopo}>
          <div>
            <h3 className={styles.modalTitulo}>Acesso ao portal de NF</h3>
            <p className={styles.modalSub}>{item.codigo} · {item.nome}</p>
          </div>
          <button type="button" className={styles.btnIconeTabela} onClick={onFechar}><FiX size={16} /></button>
        </div>

        <div className={styles.modalCorpo}>
          <label className={styles.campoLabel}>Local NF</label>
          <input className={styles.campoInput} value={form.local_nf}
            onChange={e => set('local_nf', e.target.value)}
            placeholder="Prefeitura, portal, sistema..." />

          <label className={styles.campoLabel}>Usuário</label>
          <input className={styles.campoInput} value={form.usuario}
            onChange={e => set('usuario', e.target.value)} />

          <label className={styles.campoLabel}>Senha</label>
          <div className={styles.campoComBotao}>
            <input
              className={styles.campoInput}
              type={verSenha ? 'text' : 'password'}
              value={form.senha}
              onChange={e => set('senha', e.target.value)}
              placeholder={item.tem_senha ? 'Deixe em branco pra manter a atual' : 'Digite a senha'}
              autoComplete="new-password"
            />
            <button type="button" className={styles.btnIconeTabela}
              title={verSenha ? 'Ocultar' : 'Mostrar'} onClick={() => setVerSenha(v => !v)}>
              {verSenha ? <FiEyeOff size={14} /> : <FiEye size={14} />}
            </button>
          </div>
          {item.tem_senha && (
            <p className={styles.dica}>Já existe senha salva. Só preencha se quiser trocar.</p>
          )}

          <label className={styles.campoLabel}>Link</label>
          <input className={styles.campoInput} value={form.link}
            onChange={e => set('link', e.target.value)} placeholder="https://..." />

          <label className={styles.campoLabel}>Observação</label>
          <textarea className={styles.campoTextarea} rows={3} value={form.observacao}
            onChange={e => set('observacao', e.target.value)} />

          {erro && <p className={styles.erro}>{erro}</p>}
        </div>

        <div className={styles.modalRodape}>
          <button type="button" className={styles.btnSecundario} onClick={onFechar}>Cancelar</button>
          <button type="button" className={styles.btnEmitir} onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NfClientes() {
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    return listarNfClientes()
      .then(r => setItens(r.items || []))
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const q = busca.trim().toLowerCase();
  const filtrados = q
    ? itens.filter(i => (i.nome || '').toLowerCase().includes(q) || (i.codigo || '').includes(q))
    : itens;

  return (
    <div className={`${styles.page} ${styles.pageLarga}`}>
      <div className={styles.header}>
        <div className={styles.headerIcone}><FiUsers size={20} /></div>
        <div>
          <h1 className={styles.titulo}>NF-Clientes</h1>
          <p className={styles.subtitulo}>
            Clientes que emitem a própria NF e o acesso ao portal de cada um.
            Marque quem aparece aqui em Clientes › Dados cadastrais.
          </p>
        </div>
      </div>

      <div className={`${styles.conteudo} ${styles.conteudoLargo}`}>
        <input
          className={styles.campoInput}
          style={{ maxWidth: 320, marginBottom: 12 }}
          type="search"
          placeholder="Buscar código ou cliente..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />

        {erro && <p className={styles.erro}>{erro}</p>}

        {carregando ? (
          <p className={styles.vazio}>Carregando...</p>
        ) : filtrados.length === 0 ? (
          <p className={styles.vazio}>
            {itens.length === 0
              ? 'Nenhum cliente marcado como NF-Cliente ainda — marque em Clientes › Dados cadastrais.'
              : 'Nenhum cliente encontrado com esse termo.'}
          </p>
        ) : (
          <div className={styles.tabelaWrap}>
            <table className={styles.tabela}>
              <thead>
                <tr>
                  <th className={styles.colCliente}>Cliente</th>
                  <th className={styles.colLocal}>Local NF</th>
                  <th className={styles.colUsuario}>Usuário</th>
                  <th>Senha</th>
                  <th>Link</th>
                  <th className={styles.colObs}>Observação</th>
                  <th className={styles.colAcao}></th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map(item => (
                  <tr key={item.id}>
                    <td>
                      <span className={styles.codigoCliente}>{item.codigo}</span> {item.nome}
                    </td>
                    <td className={styles.semQuebra}>{item.local_nf || <span className={styles.celulaVazia}>—</span>}</td>
                    <td className={styles.semQuebra}>
                      {item.usuario ? (
                        <div className={styles.celulaComAcoes}>
                          <span>{item.usuario}</span>
                          <BotaoCopiar aoObterTexto={() => item.usuario} titulo="Copiar usuário" />
                        </div>
                      ) : <span className={styles.celulaVazia}>—</span>}
                    </td>
                    <td><CelulaSenha item={item} aoErro={setErro} /></td>
                    <td>
                      {item.link ? (
                        <div className={styles.celulaComAcoes}>
                          <a href={item.link} target="_blank" rel="noreferrer" className={styles.linkPortal}>
                            abrir <FiExternalLink size={11} />
                          </a>
                          <BotaoCopiar aoObterTexto={() => item.link} titulo="Copiar link" />
                        </div>
                      ) : <span className={styles.celulaVazia}>—</span>}
                    </td>
                    <td className={styles.celulaObs} title={item.observacao || ''}>
                      {item.observacao || <span className={styles.celulaVazia}>—</span>}
                    </td>
                    <td>
                      <button type="button" className={styles.btnIconeTabela}
                        title="Editar acesso" onClick={() => setEditando(item)}>
                        <FiEdit3 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editando && (
        <ModalAcesso
          item={editando}
          onFechar={() => setEditando(null)}
          onSalvo={() => { setEditando(null); carregar(); }}
        />
      )}
    </div>
  );
}

export default NfClientes;
